import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import type { CreateLlmPriceRequestDto } from '../../../../backend/src/llm/pricing/dto/create-llm-price.request.dto';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

/** Global-admin only. Appends a new price version; never edits an existing one. */
export const useCreateLlmPrice = () => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  const createLlmPrice = async (data: CreateLlmPriceRequestDto) => {
    const fetchConfig = getFetchConfig({ method: FetchMethod.POST, data });

    const response = await fetch(`${APIBaseChronos}/api/admin/llm-prices`, fetchConfig);
    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as { message?: string | string[] };
      const message = Array.isArray(errorData.message) ? errorData.message.join(', ') : errorData.message;
      throw new Error(message ?? 'Failed to add price version');
    }
  };

  return useMutation({
    mutationFn: createLlmPrice,
    onSuccess: () => {
      toast.success('Price version added');
      void queryClient.invalidateQueries([QueryEntity.LlmPrices]);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};
